// src/context/BranchContext.js
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import branchService from '../services/branchService';
import { useDispatch } from 'react-redux';
import { setSelectedBranch } from '../store/pos/branchSlice';
import { RestaurantContext } from './RestaurantContext';
import { AuthContext } from './AuthContext';

const BRANCH_SCOPED_ROLES = ['branch_manager', 'chef', 'waiter', 'branch_admin'];

// ── Offline branch-list cache helpers ────────────────────────────────────────
const BRANCHES_CACHE_KEY = 'pos_branches_cache';

const saveBranchesCache = (branches, userId = null) => {
  try {
    const payload = userId ? { userId, branches } : branches; // stamped if userId known
    localStorage.setItem(BRANCHES_CACHE_KEY, JSON.stringify(payload));
  } catch {}
};

const loadBranchesCache = () => _loadBranchesCacheForCurrentUser();

export const BranchContext = createContext();

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

// ── Synchronous helpers (called during useState lazy init — no hooks allowed) ─

// Returns the stored user id synchronously (no hooks) so we can reject cached
// data that was saved by a different user on the same device.
const _getStoredUserId = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.id || null;
  } catch { return null; }
};

const _loadSelectedBranchSync = () => {
  try {
    const raw = localStorage.getItem('selectedBranch');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!(parsed?._id || parsed?.id)) return null;
    // Reject the cached branch if it was saved for a different user.
    // (Normal logout clears this, but "tab closed without logout" doesn't.)
    if (parsed._userId) {
      const storedUserId = _getStoredUserId();
      if (storedUserId && parsed._userId !== storedUserId) {
        localStorage.removeItem('selectedBranch');
        return null;
      }
    }
    return parsed;
  } catch { return null; }
};

const _loadBranchesCacheForCurrentUser = () => {
  try {
    const raw = localStorage.getItem(BRANCHES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Support both legacy format (plain array) and new format ({ userId, branches })
    if (Array.isArray(parsed)) return parsed; // legacy — accept as-is
    if (parsed?.userId) {
      const storedUserId = _getStoredUserId();
      if (storedUserId && parsed.userId !== storedUserId) {
        localStorage.removeItem(BRANCHES_CACHE_KEY);
        return null;
      }
      return Array.isArray(parsed.branches) ? parsed.branches : null;
    }
    return null;
  } catch { return null; }
};

export const BranchProvider = ({ children }) => {
  const { selectedRestaurant } = useContext(RestaurantContext);
  const { currentUser } = useContext(AuthContext);
  const dispatch = useDispatch();

  // ── A. Synchronous lazy init — data is available on the VERY FIRST render ──
  // This is the key fix: POSScreen reads contextSelectedBranch on mount;
  // if it's already non-null (from localStorage) it immediately calls
  // loadBranchData which reads posDataCache — no API call needed offline.
  const [branches, setBranches] = useState(() => _loadBranchesCacheForCurrentUser() || []);
  const [selectedBranch, setSelectedBranchState] = useState(_loadSelectedBranchSync);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isBranchScoped = currentUser && BRANCH_SCOPED_ROLES.includes(currentUser.role);

  // Track the last branch ID dispatched to Redux so we never dispatch the same
  // branch twice in one session (e.g. mount effect + branches-loaded effect both
  // try to dispatch the same ID → two socket re-inits for nothing).
  const lastDispatchedBranchIdRef = useRef(null);

  const dispatchBranchIfNew = useCallback((branch) => {
    if (!branch) return;
    const id = branch._id || branch.id;
    if (!id || id === lastDispatchedBranchIdRef.current) return;
    lastDispatchedBranchIdRef.current = id;
    dispatch(setSelectedBranch(id));
  }, [dispatch]);

  // ── B. Dispatch synchronously-restored selectedBranch to Redux on mount ─────
  useEffect(() => {
    dispatchBranchIfNew(selectedBranch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only — intentionally empty deps

  const fetchBranches = async (restaurantId = null) => {
    // ── Offline: restore from cache immediately, skip API ────────────────────
    if (!navigator.onLine) {
      const cached = loadBranchesCache();
      if (cached && cached.length > 0) {
        console.log('[BranchContext] Offline — restoring', cached.length, 'branches from cache');
        setBranches(cached);
      } else {
        console.warn('[BranchContext] Offline and no branch cache available');
      }
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await branchService.getAllBranches(restaurantId);
      setBranches(data);
      // Save to cache for next offline session
      if (data && data.length > 0) saveBranchesCache(data, currentUser?.id);
    } catch (err) {
      // API failed while online — try cache as fallback
      const cached = loadBranchesCache();
      if (cached && cached.length > 0) {
        console.warn('[BranchContext] API error — falling back to cached branches');
        setBranches(cached);
      } else {
        setError(err.message || 'Error fetching branches');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isBranchScoped) {
      fetchBranches(null);
    } else if (selectedRestaurant?._id) {
      fetchBranches(selectedRestaurant._id);
    } else if (navigator.onLine && !selectedBranch) {
      // ── D. Only clear state when online AND nothing was restored ────────────
      // Offline: keep whatever was synchronously restored from localStorage/cache
      // so the POS stays usable without internet.
      //
      // The !selectedBranch guard matters when the WAN is down but the router
      // is up (navigator.onLine still true — Ayder 2026-07-15): the restaurants
      // fetch fails, selectedRestaurant stays null, and this branch used to
      // wipe the synchronously-restored branch selection → POSScreen rendered
      // a blank screen even though every offline cache was intact. A restored
      // selection is only cleared once a real restaurant list confirms it's
      // gone (handled by the fetch paths above).
      setBranches([]);
      setSelectedBranchState(null);
    }
  }, [selectedRestaurant?._id, isBranchScoped, currentUser?.id]);

  useEffect(() => {
    if (branches.length === 0) return;
    const savedBranch = localStorage.getItem('selectedBranch');
    if (savedBranch) {
      try {
        const parsedBranch = JSON.parse(savedBranch);
        const branchExists = branches.find(b =>
          String(b._id) === String(parsedBranch._id || parsedBranch.id)
        );
        if (branchExists) {
          setSelectedBranchState(branchExists);
          dispatchBranchIfNew(branchExists); // deduped — won't fire if already dispatched on mount
        } else {
          localStorage.removeItem('selectedBranch');
          setSelectedBranchState(null);
          dispatch(setSelectedBranch(''));
          lastDispatchedBranchIdRef.current = null;
        }
      } catch {
        localStorage.removeItem('selectedBranch');
        setSelectedBranchState(null);
        dispatch(setSelectedBranch(''));
        lastDispatchedBranchIdRef.current = null;
      }
    } else if (branches.length === 1) {
      const single = branches[0];
      setSelectedBranchState(single);
      dispatchBranchIfNew(single); // deduped
      localStorage.setItem('selectedBranch', JSON.stringify({ ...single, _userId: currentUser?.id }));
    }
  }, [branches, dispatch, dispatchBranchIfNew, currentUser?.id]);
  
  // Create a new branch
  const createBranch = async (branchData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const newBranch = await branchService.createBranch(branchData);
      setBranches([...branches, newBranch]);
      return newBranch;
    } catch (err) {
      setError(err.message || 'Error creating branch');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Update a branch
  const updateBranch = async (id, branchData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const updatedBranch = await branchService.updateBranch(id, branchData);
      
      setBranches(branches.map(branch => 
        branch._id === id || branch.id === id ? updatedBranch : branch
      ));
      
      // Update selectedBranch if it's the one being updated
      if (selectedBranch && (selectedBranch._id === id || selectedBranch.id === id)) {
        setSelectedBranchState(updatedBranch);
        localStorage.setItem('selectedBranch', JSON.stringify({ ...updatedBranch, _userId: currentUser?.id }));
      }
      
      return updatedBranch;
    } catch (err) {
      setError(err.message || 'Error updating branch');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete a branch
  const deleteBranch = async (id) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await branchService.deleteBranch(id);
      setBranches(branches.filter(branch => 
        branch._id !== id && branch.id !== id
      ));
      
      // Clear selectedBranch if it's the one being deleted
      if (selectedBranch && (selectedBranch._id === id || selectedBranch.id === id)) {
        setSelectedBranchState(null);
        localStorage.removeItem('selectedBranch');
        dispatch(setSelectedBranch(''));
      }
    } catch (err) {
      setError(err.message || 'Error deleting branch');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get a single branch by ID
  const getBranchById = async (id) => {
    // First check if we already have it in state
    const existingBranch = branches.find(b => 
      b._id === id || b.id === id
    );
    
    if (existingBranch) {
      return existingBranch;
    }
    
    // Otherwise fetch from API
    setIsLoading(true);
    setError(null);
    
    try {
      const branch = await branchService.getBranchById(id);
      return branch;
    } catch (err) {
      setError(err.message || 'Error fetching branch');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Reset error state
  const clearError = () => {
    setError(null);
  };

  const selectBranch = (branch) => {
    setSelectedBranchState(branch);
    if (branch) {
      localStorage.setItem('selectedBranch', JSON.stringify({ ...branch, _userId: currentUser?.id }));
      // Always dispatch on explicit user selection (ignore dedup ref — user
      // intentionally switched branches so socket must reinit).
      const id = branch._id || branch.id;
      lastDispatchedBranchIdRef.current = id;
      dispatch(setSelectedBranch(id));
    } else {
      localStorage.removeItem('selectedBranch');
      lastDispatchedBranchIdRef.current = null;
      dispatch(setSelectedBranch(''));
    }
  };

  // Clear selected branch
  const clearSelectedBranch = () => {
    setSelectedBranchState(null);
    localStorage.removeItem('selectedBranch');
    dispatch(setSelectedBranch(''));
  };

  // Merge a freshly-fetched branch payload (typically from posService.getPosData
  // or a settings save) into the cached selectedBranch. Without this, flipping
  // a branch flag like `noPrintEnabled` in Branch Settings updates the DB but
  // the POS keeps reading the localStorage copy that doesn't carry the new
  // field — staff see the old behavior until they sign out and back in.
  const refreshSelectedBranch = useCallback((freshBranch) => {
    if (!freshBranch || typeof freshBranch !== 'object') return;
    const id = freshBranch._id || freshBranch.id;
    if (!id) return;
    setSelectedBranchState((prev) => {
      // Only merge if the fresh payload is for the same branch the device is
      // operating on (multi-branch users can have multiple POS tabs open).
      const prevId = prev?._id || prev?.id;
      if (prevId && String(prevId) !== String(id)) return prev;
      const merged = { ...(prev || {}), ...freshBranch };
      try {
        localStorage.setItem('selectedBranch', JSON.stringify({ ...merged, _userId: currentUser?.id }));
      } catch (_) { /* quota */ }
      return merged;
    });
    setBranches((list) => list.map((b) => ((b._id || b.id) === id ? { ...b, ...freshBranch } : b)));
  }, [currentUser?.id]);

  // Context value
  const value = {
    branches,
    selectedBranch,
    isLoading,
    error,
    fetchBranches,
    createBranch,
    updateBranch,
    deleteBranch,
    getBranchById,
    selectBranch,
    refreshSelectedBranch,
    clearSelectedBranch,
    clearError
  };
  
  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

export default BranchProvider;
